import React from "react";
import DateRangeFilter from "../filterDropdown/DateRangeFilter";
import FilterDropdown from "../filterDropdown/FilterDropdown";
import { formatDateRangeSummary } from "../../shared/filterUtils";

interface Stat {
  name: string;
  subtitle: string | null;
  stat: string;
  previousStat: string;
  change: string;
  changeType: string;
}

interface Analytics {
  range: string;
  stats: Stat[];
  loading: Boolean;
}

interface PackagingFilters {
  product?: {};
  category?: {};
  supervisor?: {};
  employee1?: {};
  brand?: {};
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  [key: string]: any;
}

interface StatsProps {
  analytics: Analytics;
  setFilters: (filter: PackagingFilters) => void;
  filters: PackagingFilters;
  setShowDates: (show: boolean) => void;
  showDates: boolean;
  handleSearch: () => void;
  filtersEnabled: boolean;
  showFilters: boolean;
  filterComponents: any[];
  resetFilters: () => void;
  setShowFilters: (show: boolean) => void;
  hideDatesFilter?: boolean;
}

const StatsComponents: React.FC<StatsProps> = ({
  analytics,
  setFilters,
  filters,
  setShowDates,
  showDates,
  handleSearch,
  filtersEnabled,
  showFilters,
  filterComponents,
  resetFilters,
  setShowFilters,
  hideDatesFilter = false,
}) => {
  const hasDateFilter = Boolean(filters.startDate && filters.endDate);
  const rangeSummary = formatDateRangeSummary(
    filters.startDate,
    filters.endDate,
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 rounded-[18px] border border-[#E2E8F0] bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#64748B]">
            Operational summary
          </p>
          <h3 className="mt-1 text-xl font-semibold tracking-tight text-[#0F172A]">
            {!hideDatesFilter ? rangeSummary : "Current performance"}
          </h3>
          {!hideDatesFilter && hasDateFilter ? (
            <div className="mt-2 inline-flex items-center rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-3 py-1 text-xs font-semibold text-[#2563EB]">
              Date: {rangeSummary}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          {!hideDatesFilter ? (
            <DateRangeFilter
              onChange={(startDate: Date | null, endDate: Date | null) =>
                setFilters({
                  ...filters,
                  startDate: startDate,
                  endDate: endDate,
                })
              }
              onClear={() =>
                setFilters({
                  ...filters,
                  startDate: null,
                  endDate: null,
                })
              }
              endDate={filters.endDate}
              startDate={filters.startDate}
              setShowDates={setShowDates}
              showDates={showDates}
              applyChanges={handleSearch}
              maxDate={new Date()}
              rangeSummary={rangeSummary}
            />
          ) : null}

          {filtersEnabled ? (
            <FilterDropdown
              actionCall={() => handleSearch()}
              filterComponents={filterComponents}
              resetFilters={() => {
                resetFilters();
              }}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
            />
          ) : null}
        </div>
      </div>

      <dl className="grid grid-cols-1 overflow-hidden rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:grid-cols-4 md:divide-x md:divide-[#E2E8F0]">
        {analytics.stats.map((item) => (
          <div
            key={item.name}
            className="border-b border-[#E2E8F0] p-4 last:border-b-0 md:border-b-0"
          >
            <dt className="text-sm font-medium text-[#64748B]">{item.name}</dt>
            <dd className="mt-1 flex items-baseline gap-2">
              <div className="flex-grow text-2xl font-semibold text-[#0F172A]">
                {item.stat}
              </div>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default StatsComponents;
